'use client';

import { useAffiliateLink } from '@hooks/use-affiliate-link';
import { useBetterMediaQuery } from '@hooks/use-ssr-media-query';
import { trackGa4ItemListView, trackGa4ItemSelection } from '@v2/analytics/ga4';
import { useHover } from '@uidotdev/usehooks';
import { getInternationalFullDate, getInternationalTime } from '@utils/dates';
import { v2Links } from '@utils/navigation/links';
import * as EventsSelectionList from '@v2/containers/EventsSelectionList/EventsSelectionList';
import { EventResponse } from '@v2/types';
import { ChevronRight } from '@v2/ui/Icon/Arrow';
import { cn } from '@v2/utils/cn';
import Link from 'next/link';
import { useEffect, useMemo, useRef } from 'react';
import { shouldHideLeagueEvent } from '../../util';
import { EventResultsUiLayout } from '../../constants/eventPageUiTypes';

const DAY_IN_MS = 24 * 60 * 60 * 1000;

const getRemainingDays = (occursAt: string) => {
  const today = new Date();
  const todayStart = new Date(
    today.getFullYear(),
    today.getMonth(),
    today.getDate()
  );
  const eventStart = new Date(`${occursAt.split('T')[0]}T00:00:00`);

  return Math.round((eventStart.getTime() - todayStart.getTime()) / DAY_IN_MS);
};

interface Props {
  title?: string;
  headerText?: string;
  identifier: string;
  events: EventResponse[];
  className?: string;
  shouldReturnEmpty?: boolean;
  actionLabel?: string;
  layout?: EventResultsUiLayout;
  itemListName?: string;
  itemCategory?: string;
}

export const EventsList = ({
  title,
  headerText,
  events,
  identifier,
  className,
  shouldReturnEmpty = false,
  actionLabel = 'Build package',
  layout = 'default',
  itemListName = `event_list_${identifier}`,
  itemCategory,
}: Props) => {
  const trackedListKeys = useRef(new Set<string>());
  const filteredEvents = useMemo(
    () => events?.filter((ev) => !shouldHideLeagueEvent(ev.name)) ?? [],
    [events]
  );
  const itemListId = itemListName;
  const listKey = filteredEvents
    .map((event) => `${event.id}:${event.name}`)
    .join('|');
  const trackingKey = `${itemListId}:${itemListName}:${itemCategory ?? ''}:${listKey}`;

  useEffect(() => {
    if (!filteredEvents.length || trackedListKeys.current.has(trackingKey))
      return;

    trackedListKeys.current.add(trackingKey);
    trackGa4ItemListView({
      itemListId,
      itemListName,
      items: filteredEvents.map((event, index) => ({
        item_id: String(event.id),
        item_name: event.name,
        ...(itemCategory ? { item_category: itemCategory } : {}),
        index,
      })),
    });
  }, [filteredEvents, itemCategory, itemListId, itemListName, trackingKey]);

  if (!events && !shouldReturnEmpty) return <></>;

  // Show empty state when no events are available
  if (filteredEvents.length === 0) {
    return (
      <div className={cn(className)}>
        {title && <h2 className="mb-6 text-2xl font-bold">{title}</h2>}
        {headerText && (
          <EventsSelectionList.Header>{headerText}</EventsSelectionList.Header>
        )}
      </div>
    );
  }

  return (
    <div className={cn(className)}>
      {title && <h2 className="mb-6 text-2xl font-bold">{title}</h2>}
      {headerText && (
        <EventsSelectionList.Header>{headerText}</EventsSelectionList.Header>
      )}
      <EventsSelectionList.Wrapper>
        {filteredEvents?.map((event, index) => (
          <EventCard
            key={`${identifier}-${event.id}-${event.name}`}
            event={event}
            identifier={identifier}
            actionLabel={actionLabel}
            layout={layout}
            itemListId={itemListId}
            itemListName={itemListName}
            itemCategory={itemCategory}
            index={index}
          />
        ))}
      </EventsSelectionList.Wrapper>
    </div>
  );
};

interface EventCardProps {
  event: EventResponse;
  identifier: string;
  actionLabel: string;
  layout: EventResultsUiLayout;
  itemListId: string;
  itemListName: string;
  itemCategory?: string;
  index: number;
}

export const EventCard = ({
  event,
  identifier,
  actionLabel,
  layout,
  itemListId,
  itemListName,
  itemCategory,
  index,
}: EventCardProps) => {
  const [ref, hovering] = useHover();

  const { link } = useAffiliateLink();

  const isTablet = useBetterMediaQuery('(max-width: 768px)');

  const dateFull = getInternationalFullDate(
    event.occurs_at_local,
    undefined
  ).split(', ');

  const day = dateFull[0];
  const date = dateFull[1];
  const time = getInternationalTime(event.occurs_at_local, true);
  const eventLink = event.name
    .concat(' ', day, ' ', date)
    .replaceAll(' ', '-')
    .replaceAll('.', '');

  const isVenueUnderEventLayout = layout === 'venueUnderEvent';
  const hasMobileCopyLink = Boolean(link && isTablet);
  const venueLocation = event?.venue.location || event?.venue.address?.location;
  const venueSummary = [event?.venue.name, venueLocation]
    .filter(Boolean)
    .join(' - ');
  const remainingDaysForTheEvent = getRemainingDays(event.occurs_at_local);
  const eventDescription = isVenueUnderEventLayout ? (
    <span>{venueSummary}</span>
  ) : remainingDaysForTheEvent < 2 ? (
    ['Today', 'Tomorrow'][remainingDaysForTheEvent]
  ) : (
    `In ${remainingDaysForTheEvent} days`
  );

  return (
    <div ref={ref} key={`${identifier}-${event.id}-${event.name}`}>
      <Link
        prefetch={false}
        target="_blank"
        href={`${v2Links.buy}?eventId=${event.id}&eventLink=${eventLink}`}
        onClick={() =>
          trackGa4ItemSelection({
            itemListId,
            itemListName,
            item: {
              item_id: String(event.id),
              item_name: event.name,
              ...(itemCategory ? { item_category: itemCategory } : {}),
              index,
            },
          })
        }
      >
        <EventsSelectionList.Card>
          <EventsSelectionList.Row
            className="col-span-2 md:col-span-1"
            title={date}
            description={time}
          />
          <EventsSelectionList.Row
            className={cn(
              isVenueUnderEventLayout
                ? hasMobileCopyLink
                  ? 'col-span-5 md:col-span-7'
                  : 'col-span-6 md:col-span-7'
                : 'col-span-5 md:col-span-5',
              !link && !isVenueUnderEventLayout && 'col-span-6'
            )}
            title={event.name}
            description={eventDescription}
          />

          {hasMobileCopyLink && (
            <div className="col-span-1 flex items-start justify-end">
              <EventsSelectionList.CopyLink
                triggerClassName="flex justify-end "
                eventId={event.id}
                eventLink={eventLink}
              />
            </div>
          )}

          {!isVenueUnderEventLayout ? (
            <EventsSelectionList.Row
              className="hidden md:col-span-2 md:flex md:flex-col"
              title={event?.venue.name}
              description={venueLocation}
            />
          ) : null}

          <EventsSelectionList.Action className="hidden items-center justify-end md:col-span-2 md:flex">
            {!isTablet && hovering ? (
              <EventsSelectionList.CopyLink
                eventId={event.id}
                eventLink={eventLink}
              />
            ) : (
              <div />
            )}
            <div
              className={cn(
                'flex flex-shrink-0 items-center font-semibold text-primary-500',
                link && 'ml-5'
              )}
            >
              {actionLabel}{' '}
              <ChevronRight className="ml-2" size="16" stroke="#FA2846" />
            </div>
          </EventsSelectionList.Action>
        </EventsSelectionList.Card>
      </Link>
    </div>
  );
};
